'use client';

import React, { type Dispatch, type SetStateAction } from 'react';

import { useTranslations } from 'next-intl';

import { EbayButton } from '@/components/EbayButton';
import { useEbayAuth } from '@/modules/auth/useEbayAuth';
import { fetchEbayUserCredentials } from '@/modules/login/api';
import {
  handleSSOError,
  setAccountLinkingCookies,
} from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useLoginTracking } from '@/modules/login/useLoginTracking';
import { Stdv1Stat } from '@/modules/observability/constants';
import { recordMetric } from '@/modules/observability/metrics';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { SignupType } from '@/modules/signup/types';

type Props = {
  setError: Dispatch<SetStateAction<string>>;
  setIsAccountLinkingError: Dispatch<SetStateAction<boolean>>;
};

export function EbayLoginButton({ setError, setIsAccountLinkingError }: Props) {
  const t = useTranslations('login');
  const { sendLoginSubmitAction } = useLoginTracking();
  const { ebayLoginMutation } = useLoginMutations({
    loginType: LoginType.Ebay,
  });
  const { signupWithSSO } = useSSOSignup({ ssoType: SignupType.Ebay });

  const errorMessage = t('Error.SocialProviderLogin', {
    socialProvider: 'eBay',
  });

  function handleAccountNotLinked(
    email: string,
    ebay_user_id: string,
    ebay_token_reference: string
  ) {
    setAccountLinkingCookies({ ebay_user_id, ebay_token_reference }, email);
    setIsAccountLinkingError(true);
    setError(t('SSO.EbayAccountNotLinked'));
  }

  async function handleEbayAuthorized(code: string) {
    let ebay_user_id: string;
    let ebay_token_reference: string;
    let email: string;
    let first_name: string | null | undefined;

    try {
      ({
        data: { ebay_user_id, ebay_token_reference, email, first_name },
      } = await fetchEbayUserCredentials(code));
    } catch {
      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['ebay.auth.credentials_error'],
      });
      return setError(errorMessage);
    }

    ebayLoginMutation.mutate(
      { ebay_user_id, ebay_token_reference },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError,
            onUserNotFound: () => {
              signupWithSSO({
                name: { firstName: first_name ?? '', lastName: '' },
                email,
                credentials: {
                  ebay_user_id,
                  ebay_token_reference,
                },
              });
            },
            onAccountNotLinked: () =>
              handleAccountNotLinked(email, ebay_user_id, ebay_token_reference),
          });
        },
      }
    );
  }

  const { startPopupFlow, isInProgress } = useEbayAuth({
    onEbayAuthorized: handleEbayAuthorized,
    onError: () => setError(errorMessage),
  });

  function handleClick() {
    sendLoginSubmitAction(LoginType.Ebay);
    startPopupFlow();
  }

  return (
    <EbayButton
      onClick={handleClick}
      disabled={isInProgress || ebayLoginMutation.isPending}
    />
  );
}
